import * as React from "react";
import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useLazyQuery, useMutation} from "@apollo/client";
import {
    createOrganizationInvitationMutation,
    createOrganizationMutation,
    deleteOrganizationMutation,
    getOrganizationPlansQuery,
    getOrganizationsQuery,
    setOrganizationFeatureMutation,
    setOrganizationPlanMutation,
    updateOrganizationMutation
} from "../../components/area/areaQueries";
import {UserContext, UserContextI} from "../../UserContext";
import {useConfirmDialog} from "../../components/common/ConfirmDialogProvider";
import {
    Button,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Grid,
    HStack,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useToast
} from "@chakra-ui/react";
import {BiArrowBack, BiCog, BiEdit, BiPlus, BiTrashAlt} from "react-icons/bi";
import AuthButton from "../../auth/AuthButton";
import {useSearchParams} from "react-router-dom";
import {isRootAdminRole} from "../../auth/types";
import {OrganizationPlan, OrganizationRoleUsage} from "../../components/types";

type OrganizationRow = {
    id: string
    name: string
    appVersion?: string
    features: string[]
    rolePlan?: OrganizationPlan
    roleUsage?: OrganizationRoleUsage
}

type DialogMode = "create" | "edit";
type InviteDialogState = {
    organizationId: string
    organizationName: string
}

const isValidInviteEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());
const CUE_LIST_FEATURE = "CUE_LIST";
const EXT_LANG_FEATURE = "EXT_LANG";

const OrganizationManagement = () => {
    const userContext: UserContextI = useContext<UserContextI>(UserContext);
    const {confirm} = useConfirmDialog();
    const toast = useToast();
    const [search, setSearch] = useSearchParams();

    const [getOrganizations, {data: organizationsData, loading: loadingOrganizations}] = useLazyQuery(getOrganizationsQuery);
    const [getOrganizationPlans, {data: organizationPlansData, loading: loadingPlans}] = useLazyQuery(getOrganizationPlansQuery);
    const [createOrganization, {loading: creatingOrganization}] = useMutation(createOrganizationMutation);
    const [updateOrganization, {loading: updatingOrganization}] = useMutation(updateOrganizationMutation);
    const [deleteOrganization, {loading: deletingOrganization}] = useMutation(deleteOrganizationMutation);
    const [createOrganizationInvitation, {loading: creatingInvitation}] = useMutation(createOrganizationInvitationMutation);
    const [setOrganizationFeature, {loading: updatingFeature}] = useMutation(setOrganizationFeatureMutation);
    const [setOrganizationPlan, {loading: updatingPlanAssignment}] = useMutation(setOrganizationPlanMutation);

    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [dialogMode, setDialogMode] = useState<DialogMode>("create");
    const [editingOrganizationId, setEditingOrganizationId] = useState<string>("");
    const [organizationName, setOrganizationName] = useState<string>("");
    const [selectedPlanId, setSelectedPlanId] = useState<string>("");
    const [selectedAppVersion, setSelectedAppVersion] = useState<string>("3.15");
    const [cueListEnabled, setCueListEnabled] = useState<boolean>(false);
    const [extendedLanguagesEnabled, setExtendedLanguagesEnabled] = useState<boolean>(false);
    const [inviteDialog, setInviteDialog] = useState<InviteDialogState | null>(null);
    const [inviteEmail, setInviteEmail] = useState<string>("");

    const user = userContext.getUser();
    const isRootAdmin = isRootAdminRole(user?.role);

    const organizations = useMemo<OrganizationRow[]>(() => {
        const list = organizationsData?.getOrganizations || [];
        return list
            .map((org: any) => ({
                id: org?.id,
                name: org?.name || "",
                appVersion: org?.appVersion || undefined,
                features: Array.isArray(org?.features) ? org.features : [],
                rolePlan: org?.rolePlan ? {
                    id: org.rolePlan.id,
                    name: org.rolePlan.name,
                    maxAdministrators: org.rolePlan.maxAdministrators,
                    maxEditors: org.rolePlan.maxEditors,
                    isDefault: org.rolePlan.isDefault === true
                } : undefined,
                roleUsage: org?.roleUsage ? {
                    administrators: org.roleUsage.administrators || 0,
                    editors: org.roleUsage.editors || 0
                } : undefined
            }))
            .filter((org: OrganizationRow) => !!org.id)
            .sort((a: OrganizationRow, b: OrganizationRow) => a.name.localeCompare(b.name, undefined, {sensitivity: "base"}));
    }, [organizationsData]);

    const organizationPlans = useMemo<OrganizationPlan[]>(() => {
        const list = organizationPlansData?.getOrganizationPlans || [];
        return list
            .map((plan: any) => ({
                id: plan?.id,
                name: plan?.name || "",
                maxAdministrators: plan?.maxAdministrators ?? 0,
                maxEditors: plan?.maxEditors ?? 0,
                isDefault: plan?.isDefault === true
            }))
            .filter((plan: OrganizationPlan) => !!plan.id)
            .sort((a: OrganizationPlan, b: OrganizationPlan) => a.name.localeCompare(b.name, undefined, {sensitivity: "base"}));
    }, [organizationPlansData]);

    const loadOrganizations = useCallback(() => {
        if (!isRootAdmin) {
            return;
        }
        getOrganizations({
            variables: {
                organizationIds: []
            },
            fetchPolicy: "network-only"
        });
        getOrganizationPlans({
            fetchPolicy: "network-only"
        });
    }, [getOrganizationPlans, getOrganizations, isRootAdmin]);

    useEffect(() => {
        loadOrganizations();
    }, [loadOrganizations]);

    useEffect(() => {
        if (!dialogOpen || selectedPlanId || organizationPlans.length === 0) {
            return;
        }
        setSelectedPlanId(organizationPlans[0].id);
    }, [dialogOpen, organizationPlans, selectedPlanId]);

    const closeDialog = () => {
        if (creatingOrganization || updatingOrganization || updatingPlanAssignment || updatingFeature) {
            return;
        }
        setDialogOpen(false);
        setEditingOrganizationId("");
        setOrganizationName("");
        setSelectedPlanId(organizationPlans[0]?.id || "");
        setSelectedAppVersion("3.15");
        setCueListEnabled(false);
        setExtendedLanguagesEnabled(false);
        setDialogMode("create");
    };

    const openCreateDialog = () => {
        setDialogMode("create");
        setEditingOrganizationId("");
        setOrganizationName("");
        setSelectedPlanId(organizationPlans[0]?.id || "");
        setSelectedAppVersion("3.15");
        setCueListEnabled(false);
        setExtendedLanguagesEnabled(false);
        setDialogOpen(true);
    };

    const openEditDialog = (organization: OrganizationRow) => {
        setDialogMode("edit");
        setEditingOrganizationId(organization.id);
        setOrganizationName(organization.name);
        setSelectedPlanId(organization.rolePlan?.id || organizationPlans[0]?.id || "");
        setSelectedAppVersion(organization.appVersion || "3.15");
        setCueListEnabled(organization.features.includes(CUE_LIST_FEATURE));
        setExtendedLanguagesEnabled(organization.features.includes(EXT_LANG_FEATURE));
        setDialogOpen(true);
    };

    const backToAreas = () => {
        search.delete("view");
        setSearch(search);
    };

    const openPlanManagement = () => {
        search.set("view", "plans");
        setSearch(search);
    };

    const openOrganizationManagement = () => {
        search.set("view", "organizations");
        setSearch(search);
    };

    const openUserManagement = () => {
        search.set("view", "users");
        setSearch(search);
    };

    const handleSaveOrganization = async () => {
        const trimmedName = organizationName.trim();
        if (!trimmedName) {
            return;
        }
        try {
            if (dialogMode === "create") {
                const response = await createOrganization({
                    variables: {
                        name: trimmedName,
                        appVersion: selectedAppVersion
                    }
                });
                const organizationId = response.data?.createOrganization?.id;
                if (organizationId) {
                    if (selectedPlanId) {
                        await setOrganizationPlan({
                            variables: {
                                organizationId: organizationId,
                                organizationPlanId: selectedPlanId
                            }
                        });
                    }
                    await setOrganizationFeature({
                        variables: {
                            organizationId: organizationId,
                            featureId: CUE_LIST_FEATURE,
                            enabled: cueListEnabled
                        }
                    });
                    await setOrganizationFeature({
                        variables: {
                            organizationId: organizationId,
                            featureId: EXT_LANG_FEATURE,
                            enabled: extendedLanguagesEnabled
                        }
                    });
                }
            } else {
                await updateOrganization({
                    variables: {
                        id: editingOrganizationId,
                        name: trimmedName,
                        appVersion: selectedAppVersion
                    }
                });
                if (selectedPlanId) {
                    await setOrganizationPlan({
                        variables: {
                            organizationId: editingOrganizationId,
                            organizationPlanId: selectedPlanId
                        }
                    });
                }
                await setOrganizationFeature({
                    variables: {
                        organizationId: editingOrganizationId,
                        featureId: CUE_LIST_FEATURE,
                        enabled: cueListEnabled
                    }
                });
                await setOrganizationFeature({
                    variables: {
                        organizationId: editingOrganizationId,
                        featureId: EXT_LANG_FEATURE,
                        enabled: extendedLanguagesEnabled
                    }
                });
            }
            closeDialog();
            loadOrganizations();
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : "Unable to save organization.";
            toast({
                title: "Organization save failed",
                description: message,
                status: "error",
                isClosable: true
            });
        }
    };

    const handleDeleteOrganization = async (organization: OrganizationRow) => {
        const approved = await confirm({
            title: "Delete organization",
            body: `Delete "${organization.name}"? This only works if no spaces are linked to it.`,
            confirmText: "Delete",
            isDestructive: true
        });
        if (!approved) {
            return;
        }
        try {
            await deleteOrganization({
                variables: {
                    id: organization.id
                }
            });
            loadOrganizations();
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : "Unable to delete organization.";
            toast({
                title: "Organization delete failed",
                description: message,
                status: "error",
                isClosable: true
            });
        }
    };

    const openInviteDialog = (organization: OrganizationRow) => {
        setInviteDialog({
            organizationId: organization.id,
            organizationName: organization.name
        });
        setInviteEmail("");
    };

    const closeInviteDialog = () => {
        if (creatingInvitation) {
            return;
        }
        setInviteDialog(null);
        setInviteEmail("");
    };

    const handleInvite = async (action: "send" | "copy") => {
        if (!inviteDialog) {
            return;
        }
        const trimmedEmail = inviteEmail.trim();
        if (!isValidInviteEmail(trimmedEmail) || !trimmedEmail) {
            return;
        }
        try {
            const response = await createOrganizationInvitation({
                variables: {
                    organizationId: inviteDialog.organizationId,
                    email: trimmedEmail
                }
            });
            const token = response.data?.createOrganizationInvitation?.token;
            const expiresAt = response.data?.createOrganizationInvitation?.expiresAt;
            const emailSent = response.data?.createOrganizationInvitation?.emailSent === true;
            if (!token) {
                toast({
                    title: "Invitation failed",
                    description: "Invite token missing.",
                    status: "error",
                    isClosable: true
                });
                return;
            }
            const inviteUrl = `${window.location.origin}/invite?token=${encodeURIComponent(token)}`;
            const expiresLabel = expiresAt ? ` Expires ${new Date(expiresAt).toLocaleString()}.` : "";

            if (action === "send" && emailSent) {
                toast({
                    title: "Invitation sent",
                    description: `Emailed invitation to ${trimmedEmail}.${expiresLabel}`,
                    status: "success",
                    isClosable: true
                });
                closeInviteDialog();
                return;
            }

            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(inviteUrl);
            } else {
                const input = document.createElement("input");
                input.value = inviteUrl;
                document.body.appendChild(input);
                input.select();
                document.execCommand("copy");
                document.body.removeChild(input);
            }

            if (action === "send" && !emailSent) {
                toast({
                    title: "Couldn't send email — link copied",
                    description: `Email delivery failed for ${trimmedEmail}. The invite link was copied to your clipboard so you can share it manually.${expiresLabel}`,
                    status: "warning",
                    isClosable: true,
                    duration: 8000
                });
            } else {
                toast({
                    title: "Invitation link copied",
                    description: `Copied one-time invite link for ${trimmedEmail}.${expiresLabel}`,
                    status: "success",
                    isClosable: true
                });
            }
            closeInviteDialog();
        } catch (error) {
            console.error(error);
            const message = error instanceof Error ? error.message : "Unable to create invitation link.";
            toast({
                title: "Invitation failed",
                description: message,
                status: "error",
                isClosable: true
            });
        }
    };

    if (!isRootAdmin) {
        return <Grid width={"inherit"} id={"Grid"} gridTemplateRows={"40px 1fr 40px"}>
            <Grid gridTemplateColumns={"1fr max-content"} alignItems={"center"} columnGap={2}>
                <Button size={"sm"} leftIcon={<BiArrowBack/>} onClick={backToAreas}>
                    Back to spaces
                </Button>
                <AuthButton/>
            </Grid>
            <div>Unauthorized</div>
            <div/>
        </Grid>;
    }

    const isNameInvalid = organizationName.trim().length === 0;

    return <Grid width={"inherit"} id={"Grid"} gridTemplateRows={"40px minmax(0, 1fr) 40px"}>
        <Grid gridTemplateColumns={"max-content 1fr max-content max-content"} alignItems={"center"} columnGap={2}>
            <Button size={"sm"} leftIcon={<BiArrowBack/>} onClick={backToAreas} width={"fit-content"} justifySelf={"start"}>
                Back to spaces
            </Button>
            <div/>
            <Menu>
                <MenuButton
                    as={IconButton}
                    aria-label={"Management actions"}
                    icon={<BiCog/>}
                    size={"sm"}
                    variant={"outline"}
                />
                <MenuList zIndex={20}>
                    <MenuItem onClick={openOrganizationManagement}>
                        Manage organizations
                    </MenuItem>
                    <MenuItem onClick={openUserManagement}>
                        Manage users
                    </MenuItem>
                    <MenuItem onClick={openPlanManagement}>
                        Manage organization plans
                    </MenuItem>
                </MenuList>
            </Menu>
            <AuthButton/>
        </Grid>
        <Grid minH={0} gap={6} overflowY={"auto"}>
            <Stack spacing={6}>
                <Stack spacing={3}>
                    <HStack justifyContent={"space-between"} alignItems={"center"}>
                        <Text fontSize={"lg"} fontWeight={"semibold"}>Organizations</Text>
                        <IconButton
                            aria-label={"Add organization"}
                            icon={<BiPlus/>}
                            size={"sm"}
                            variant={"ghost"}
                            onClick={openCreateDialog}
                        />
                    </HStack>
                    <Table size={"sm"} variant={"simple"}>
                        <Thead>
                            <Tr>
                                <Th>Organization</Th>
                                <Th>App version</Th>
                                <Th>Plan</Th>
                                <Th>Usage</Th>
                                <Th>Features</Th>
                                <Th width={"1%"} whiteSpace={"nowrap"}>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {organizations.map((organization: OrganizationRow) => (
                                <Tr key={organization.id}>
                                    <Td>{organization.name}</Td>
                                    <Td>{organization.appVersion || "3.15"}</Td>
                                    <Td>{organization.rolePlan?.name || "No plan"}</Td>
                                    <Td whiteSpace={"nowrap"}>
                                        {organization.roleUsage
                                            ? `${organization.roleUsage.administrators}/${organization.rolePlan?.maxAdministrators ?? 0} admins, ${organization.roleUsage.editors}/${organization.rolePlan?.maxEditors ?? 0} editors`
                                            : "No users"}
                                    </Td>
                                    <Td>{organization.features.length > 0 ? organization.features.join(", ") : "None"}</Td>
                                    <Td>
                                        <HStack spacing={1}>
                                            <Tooltip label={"Create invitation link"}>
                                                <Button size={"xs"} variant={"ghost"} onClick={() => openInviteDialog(organization)}>
                                                    Invite user
                                                </Button>
                                            </Tooltip>
                                            <Tooltip label={"Edit organization"}>
                                                <IconButton
                                                    aria-label={"Edit organization"}
                                                    icon={<BiEdit/>}
                                                    size={"sm"}
                                                    variant={"ghost"}
                                                    onClick={() => openEditDialog(organization)}
                                                />
                                            </Tooltip>
                                            <Tooltip label={"Delete organization"}>
                                                <IconButton
                                                    aria-label={"Delete organization"}
                                                    icon={<BiTrashAlt/>}
                                                    size={"sm"}
                                                    variant={"ghost"}
                                                    colorScheme={"red"}
                                                    onClick={() => handleDeleteOrganization(organization)}
                                                    isDisabled={deletingOrganization}
                                                />
                                            </Tooltip>
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                            {organizations.length === 0 && !loadingOrganizations ? (
                                <Tr>
                                    <Td colSpan={5}>No organizations</Td>
                                </Tr>
                            ) : null}
                        </Tbody>
                    </Table>
                </Stack>

            </Stack>
        </Grid>
        <div/>

        <Modal isOpen={dialogOpen} onClose={closeDialog} isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>{dialogMode === "create" ? "Add organization" : "Edit organization"}</ModalHeader>
                <ModalCloseButton isDisabled={creatingOrganization || updatingOrganization}/>
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl isInvalid={isNameInvalid}>
                            <FormLabel>Name</FormLabel>
                            <Input
                                value={organizationName}
                                onChange={(event) => setOrganizationName(event.target.value)}
                                placeholder={"Organization name"}
                                autoFocus
                            />
                            {isNameInvalid ? (
                                <FormErrorMessage>Name is required.</FormErrorMessage>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <FormLabel>App version</FormLabel>
                            <Select
                                value={selectedAppVersion}
                                onChange={(event) => setSelectedAppVersion(event.target.value)}
                            >
                                <option value="3.15">3.15</option>
                                <option value="4.1">4.1</option>
                                <option value="4.2">4.2</option>
                                <option value="5.0">5.0</option>
                                <option value="6.1">6.1</option>
                                <option value="6.2">6.2</option>
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Plan</FormLabel>
                            <Select
                                value={selectedPlanId}
                                onChange={(event) => setSelectedPlanId(event.target.value)}
                                isDisabled={loadingPlans || organizationPlans.length === 0}
                            >
                                {organizationPlans.map((plan: OrganizationPlan) => (
                                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel>Features</FormLabel>
                            <Stack spacing={3}>
                                <Checkbox
                                    isChecked={cueListEnabled}
                                    onChange={(event) => setCueListEnabled(event.target.checked)}
                                >
                                    Cue List
                                </Checkbox>
                                <Checkbox
                                    isChecked={extendedLanguagesEnabled}
                                    onChange={(event) => setExtendedLanguagesEnabled(event.target.checked)}
                                >
                                    Ext. lang
                                </Checkbox>
                            </Stack>
                        </FormControl>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant='ghost' mr={3} onClick={closeDialog} isDisabled={creatingOrganization || updatingOrganization}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme='blue'
                        onClick={handleSaveOrganization}
                        isLoading={creatingOrganization || updatingOrganization || updatingPlanAssignment || updatingFeature}
                        isDisabled={isNameInvalid || !selectedPlanId}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>

        <Modal isOpen={!!inviteDialog} onClose={closeInviteDialog} isCentered>
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Invite user</ModalHeader>
                <ModalCloseButton isDisabled={creatingInvitation}/>
                <ModalBody>
                    <FormControl>
                        <FormLabel>Organization</FormLabel>
                        <Input value={inviteDialog?.organizationName || ""} isReadOnly/>
                    </FormControl>
                    <FormControl isInvalid={!isValidInviteEmail(inviteEmail)} mt={4}>
                        <FormLabel>User email</FormLabel>
                        <Input
                            value={inviteEmail}
                            onChange={(event) => setInviteEmail(event.target.value)}
                            placeholder={"name@example.com"}
                            autoFocus
                        />
                        <FormHelperText>
                            The invitation link can only be used by this email.
                        </FormHelperText>
                        {!isValidInviteEmail(inviteEmail) ? (
                            <FormErrorMessage>Enter a valid email address.</FormErrorMessage>
                        ) : null}
                    </FormControl>
                </ModalBody>
                <ModalFooter>
                    <Button variant={"ghost"} mr={3} onClick={closeInviteDialog} isDisabled={creatingInvitation}>
                        Cancel
                    </Button>
                    <Button
                        variant={"outline"}
                        mr={3}
                        onClick={() => handleInvite("copy")}
                        isLoading={creatingInvitation}
                        isDisabled={!isValidInviteEmail(inviteEmail) || !inviteEmail.trim()}
                    >
                        Copy link
                    </Button>
                    <Button
                        colorScheme={"teal"}
                        onClick={() => handleInvite("send")}
                        isLoading={creatingInvitation}
                        isDisabled={!isValidInviteEmail(inviteEmail) || !inviteEmail.trim()}
                    >
                        Send invitation
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    </Grid>;
}

export default OrganizationManagement;
